import React, { Children, cloneElement, isValidElement, type ReactNode } from 'react'
import { View } from 'react-native'
import { twMerge } from 'tailwind-merge'

export type ChipGroupProps = {
    children: ReactNode
    className?: string
    testID?: string
}

/**
 * A segmented control: the chips sit flush, and only the outer edges of the row
 * are rounded. Figma says so through four corner overrides on the two chip
 * instances — `chipgroup.contract.ts` carries them, and
 * `chipgroup.contract.spec.ts` asserts this component reproduces every one.
 *
 * A chip on its own rounds all four corners, so the group has to square off the
 * edges that meet a neighbour. That is why the corner classes are appended to
 * the child rather than set on the wrapper: the radius belongs to the chip's
 * own surface, and a wrapper would clip nothing.
 */
const cornersFor = (index: number, count: number): string => {
    if (count === 1) {
        return ''
    }
    if (index === 0) {
        return 'rounded-l-common rounded-r-none'
    }
    if (index === count - 1) {
        return 'rounded-r-common rounded-l-none'
    }
    return 'rounded-none'
}

export const ChipGroup = ({ children, className, testID }: ChipGroupProps) => {
    const items = Children.toArray(children)
    return (
        <View
            className={twMerge('flex-row items-center', className)}
            testID={testID ?? 'chip-group'}
        >
            {items.map((child, index) => (
                <View className='flex-1' key={index}>
                    {isValidElement<{ className?: string }>(child)
                        ? cloneElement(child, {
                              className: twMerge(
                                  child.props.className,
                                  cornersFor(index, items.length),
                              ),
                          })
                        : child}
                </View>
            ))}
        </View>
    )
}

ChipGroup.displayName = 'ChipGroup'
